import React, { useEffect, useState } from 'react';
import './Groups.css';
import { api, endpoints } from '../config/api';
import { Button, Alert, Spinner } from '../components/ui';
import { useT } from '../i18n';

const messages = {
  docTitle: { tr: 'Gruplar - Signature Manager', en: 'Groups - Signature Manager' },
  title: { tr: 'Gruplar', en: 'Groups' },
  hint: {
    tr: 'İmza şablonu atanabilecek grupları burada görünür veya gizli olarak işaretleyin. Yalnızca görünür gruplar şablon atamasında listelenir.',
    en: 'Mark which groups can be assigned a signature template as visible or hidden. Only visible groups are listed when assigning templates.',
  },
  search: { tr: 'Grup adı ara', en: 'Search group name' },
  error: { tr: 'Hata: {msg}', en: 'Error: {msg}' },
  colName: { tr: 'Grup Adı', en: 'Group Name' },
  colCount: { tr: 'Atanan Şablon', en: 'Assigned Templates' },
  colVisible: { tr: 'Görünür', en: 'Visible' },
  empty: { tr: 'Grup bulunamadı.', en: 'No groups found.' },
  templateCount: { tr: '{n} şablon', en: ({ n }) => `${n} template${n === 1 ? '' : 's'}` },
  syncNote: {
    tr: 'Gruplar bağlı dizin sağlayıcınızdan (Microsoft 365 / Google Workspace) otomatik senkronize edilir.',
    en: 'Groups are synchronized automatically from your connected directory provider (Microsoft 365 / Google Workspace).',
  },
};

export default function Groups() {
  const { t } = useT(messages);
  useEffect(() => {
    document.title = t('docTitle');
  }, [t]);

  const [groups, setGroups] = useState([]);
  const [localGroups, setLocalGroups] = useState([]); // local değişiklikler için
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [changed, setChanged] = useState(false); // değişiklik var mı?
  const [search, setSearch] = useState('');

  // Grup başına atanmış şablon sayısı — tasarımdaki "Atanan Şablon"
  // sütunu için, imza şablonlarının assignments listesinden hesaplanır.
  const [templateCounts, setTemplateCounts] = useState({});

  const fetchGroups = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get(endpoints.signatures.groups.visibility);
      setGroups(data);
      setLocalGroups(data); // local state'i de güncelle
      setChanged(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplateCounts = async () => {
    try {
      const templates = await api.get(endpoints.signatures.templates.list);
      const counts = {};
      (templates || []).forEach(t => {
        (t.assignments || []).forEach(a => {
          counts[a.group_name] = (counts[a.group_name] || 0) + 1;
        });
      });
      setTemplateCounts(counts);
    } catch (err) {
      setTemplateCounts({});
    }
  };

  useEffect(() => { fetchGroups(); fetchTemplateCounts(); }, []);

  const filteredGroups = localGroups.filter(g =>
    g.group_name.toLowerCase().includes(search.toLowerCase())
  );

  // Checkbox değişikliğinde sadece localGroups güncellenir
  const handleLocalToggle = (group_name, visible) => {
    setLocalGroups(prev => prev.map(g =>
      g.group_name === group_name ? { ...g, visible } : g
    ));
    setChanged(true);
  };

  // Kaydet butonuna basınca topluca API'ye gönder
  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      for (const g of localGroups) {
        const original = groups.find(orig => orig.group_name === g.group_name);
        if (original && original.visible !== g.visible) {
          await api.post(endpoints.signatures.groups.visibility, {
            group_name: g.group_name,
            visible: g.visible
          });
        }
      }
      await fetchGroups();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page-stack">
      <div className="groups-header">
        <div>
          <h1>{t('title')}</h1>
          <p className="groups-hint">{t('hint')}</p>
        </div>
      </div>

      <div className="groups-toolbar">
        <div className="groups-search">
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
            <circle cx="11" cy="11" r="7" /><path d="M21 21l-4-4" />
          </svg>
          <input
            type="text"
            placeholder={t('search')}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <Button onClick={handleSave} disabled={!changed || saving}>
          {saving ? t('saving') : t('save')}
        </Button>
      </div>

      {error && <Alert variant="error">{t('error', { msg: error })}</Alert>}

      {loading ? (
        <div className="page-loading"><Spinner /></div>
      ) : (
        <div className="groups-card">
          <div className="groups-row groups-row-head">
            <span className="groups-col-name">{t('colName')}</span>
            <span className="groups-col-count">{t('colCount')}</span>
            <span className="groups-col-visible">{t('colVisible')}</span>
          </div>
          {filteredGroups.length === 0 && (
            <div className="groups-empty">{t('empty')}</div>
          )}
          {filteredGroups.map(g => {
            const count = templateCounts[g.group_name] || 0;
            return (
              <div className={`groups-row${!g.visible ? ' is-hidden' : ''}`} key={g.group_name}>
                <span className="groups-col-name">{g.group_name}</span>
                <span className="groups-col-count">{count > 0 ? t('templateCount', { n: count }) : '—'}</span>
                <span className="groups-col-visible">
                  <label className="groups-toggle">
                    <input
                      type="checkbox"
                      checked={g.visible}
                      disabled={saving}
                      onChange={e => handleLocalToggle(g.group_name, e.target.checked)}
                    />
                    <span className="groups-toggle-track"><span className="groups-toggle-knob" /></span>
                  </label>
                </span>
              </div>
            );
          })}
        </div>
      )}
      <p className="groups-sync-note">{t('syncNote')}</p>
    </div>
  );
}
